/**
 * Assessment Progress Service
 * Manages local storage persistence for in-progress assessments
 */

import { Log } from '@/utils/logger';

export interface AssessmentProgress {
  assessmentId: string;
  userId: string;
  responses: Record<string, string>;
  timestamp: number;
  assessmentTitle?: string;
}

const STORAGE_PREFIX = 'assessment_progress_';
const EXPIRY_DAYS = 30;

/**
 * Generate storage key for a specific user and assessment
 */
const getStorageKey = (userId: string, assessmentId: string): string => {
  return `${STORAGE_PREFIX}${userId}_${assessmentId}`;
};

/**
 * Check if stored progress has expired
 */
const isExpired = (timestamp: number): boolean => {
  const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - timestamp > expiryMs;
};

/**
 * Save assessment progress to local storage
 */
export const saveAssessmentProgress = (
  userId: string,
  assessmentId: string,
  responses: Record<string, string>,
  assessmentTitle?: string
): boolean => {
  try {
    const progress: AssessmentProgress = {
      assessmentId,
      userId,
      responses,
      timestamp: Date.now(),
      assessmentTitle,
    };

    const key = getStorageKey(userId, assessmentId);
    localStorage.setItem(key, JSON.stringify(progress));
    return true;
  } catch (error) {
    Log.error('Failed to save assessment progress', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return false;
  }
};

/**
 * Load assessment progress from local storage
 */
export const loadAssessmentProgress = (
  userId: string,
  assessmentId: string
): Record<string, string> | null => {
  try {
    const key = getStorageKey(userId, assessmentId);
    const stored = localStorage.getItem(key);

    if (!stored) {
      return null;
    }

    const progress: AssessmentProgress = JSON.parse(stored);

    // Validate data integrity
    if (!progress.responses || !progress.timestamp || !progress.userId) {
      Log.error('Invalid progress data, clearing localStorage entry');
      localStorage.removeItem(key);
      return null;
    }

    // Check if expired
    if (isExpired(progress.timestamp)) {
      Log.action('Progress expired, clearing localStorage entry');
      localStorage.removeItem(key);
      return null;
    }

    // Verify it's for the correct user and assessment
    if (progress.userId !== userId || progress.assessmentId !== assessmentId) {
      Log.error('Progress mismatch, clearing localStorage entry');
      localStorage.removeItem(key);
      return null;
    }

    return progress.responses;
  } catch (error) {
    Log.error('Failed to load assessment progress', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return null;
  }
};

/**
 * Clear specific assessment progress
 */
export const clearAssessmentProgress = (
  userId: string,
  assessmentId: string
): void => {
  try {
    const key = getStorageKey(userId, assessmentId);
    localStorage.removeItem(key);
  } catch (error) {
    Log.error('Failed to clear assessment progress', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};

/**
 * Get all in-progress assessments for a user
 */
export const getAllUserProgress = (userId: string): AssessmentProgress[] => {
  try {
    const allProgress: AssessmentProgress[] = [];
    const prefix = `${STORAGE_PREFIX}${userId}_`;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            const progress: AssessmentProgress = JSON.parse(stored);
            if (!isExpired(progress.timestamp)) {
              allProgress.push(progress);
            } else {
              localStorage.removeItem(key);
            }
          } catch {
            // Skip invalid entries
            localStorage.removeItem(key);
          }
        }
      }
    }

    return allProgress;
  } catch (error) {
    Log.error('Failed to get user progress', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return [];
  }
};

/**
 * Clear all expired assessment progress across all users
 */
export const clearExpiredAssessmentProgress = (): void => {
  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            const progress: AssessmentProgress = JSON.parse(stored);
            if (isExpired(progress.timestamp)) {
              keysToRemove.push(key);
            }
          } catch {
            // Remove corrupted entries
            keysToRemove.push(key);
          }
        }
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    if (keysToRemove.length > 0) {
      Log.action(`Cleared ${keysToRemove.length} expired/invalid assessment progress entries`);
    }
  } catch (error) {
    Log.error('Failed to clear expired progress', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};
